import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, Images, X, RotateCcw, Plus, Check, AlertCircle, Loader2, Heart, FileVideo, ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { uploadFile, getGuestName, setGuestName, uuid } from "@/lib/api";

const ACCEPT = "image/*,video/*,.heic,.heif,.mov,.mp4,.jpg,.jpeg,.png,.webp";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function Preview({ f }) {
  const [broken, setBroken] = useState(false);
  const isVideo = f.file.type.startsWith("video") || /\.(mov|mp4|m4v)$/i.test(f.file.name);
  if (broken) return <div className="w-full h-full grid place-items-center text-[#968E87]">{isVideo ? <FileVideo /> : <ImageIcon />}</div>;
  return isVideo
    ? <video src={f.url} muted playsInline preload="metadata" className="w-full h-full object-cover" onError={() => setBroken(true)} />
    : <img src={f.url} alt="" className="w-full h-full object-cover" onError={() => setBroken(true)} />;
}

export default function UploadFlow({ open, onClose, slug, wedding, token, onUploaded, onShowGallery }) {
  const [step, setStep] = useState("choose");
  const [files, setFiles] = useState([]);
  const [name, setName] = useState(getGuestName());
  const [editName, setEditName] = useState(!getGuestName());
  const [running, setRunning] = useState(false);
  const camRef = useRef();
  const galRef = useRef();
  const fromCamera = useRef(false);

  useEffect(() => {
    if (!running) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [running]);

  const add = (list, replaceLast = false) => {
    const next = Array.from(list).map((file) => ({ id: uuid(), file, url: URL.createObjectURL(file), status: "pending", progress: 0, error: null }));
    if (!next.length) return;
    setFiles((cur) => (replaceLast ? [...cur.slice(0, -1), ...next] : [...cur, ...next]));
    setStep("review");
  };

  const pick = (ref, cam, retake = false) => {
    fromCamera.current = cam;
    ref.current.value = "";
    ref.current.dataset.retake = retake ? "1" : "";
    ref.current.click();
  };

  const remove = (id) => setFiles((cur) => { const n = cur.filter((f) => f.id !== id); if (!n.length) setStep("choose"); return n; });
  const patch = (id, p) => setFiles((cur) => cur.map((f) => (f.id === id ? { ...f, ...p } : f)));

  const start = async () => {
    const n = name.trim();
    if (n.length < 2) { setEditName(true); return; }
    setGuestName(n);
    setEditName(false);
    setStep("uploading");
    setRunning(true);
    const queue = files.filter((f) => f.status !== "done");
    for (const f of queue) {
      patch(f.id, { status: "uploading", progress: 0, error: null });
      let ok = false;
      for (let attempt = 0; attempt < 4 && !ok; attempt++) {
        try {
          await uploadFile(slug, f.file, n, token, (p) => patch(f.id, { progress: p }));
          ok = true;
          patch(f.id, { status: "done", progress: 1 });
          onUploaded?.();
        } catch (e) {
          const retryable = e.status === 0 || e.status >= 500 || e.status === 429;
          if (!retryable || attempt === 3) { patch(f.id, { status: "error", error: e.message }); break; }
          patch(f.id, { error: `Ponovni poskus ${attempt + 1} …` });
          await sleep(2000 * 2 ** attempt);
        }
      }
    }
    setRunning(false);
  };

  useEffect(() => {
    if (step === "uploading" && !running && files.length && files.every((f) => f.status === "done")) setStep("done");
  }, [step, running, files]);

  const reset = () => { files.forEach((f) => URL.revokeObjectURL(f.url)); setFiles([]); setStep("choose"); };
  const close = () => { if (running) return; if (step === "done") reset(); onClose(); };

  const doneCount = files.filter((f) => f.status === "done").length;
  const failed = files.filter((f) => f.status === "error");
  const current = Math.min(files.length, doneCount + (running ? 1 : 0));
  const overall = files.length ? files.reduce((s, f) => s + (f.status === "done" ? 1 : f.progress), 0) / files.length : 0;
  const allPhotos = files.every((f) => f.file.type.startsWith("image"));

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-[#231F20]/40 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="upload-flow">
          <motion.div initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="w-full sm:max-w-lg bg-[#FAF7F2] rounded-t-[2rem] sm:rounded-[2rem] max-h-[94vh] overflow-y-auto pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            <div className="sticky top-0 bg-[#FAF7F2]/95 backdrop-blur z-10 flex items-center justify-between px-6 pt-5 pb-3">
              <p className="eyebrow">{wedding.title}</p>
              <button onClick={close} disabled={running} className="p-2 rounded-full hover:bg-[#EADBCE] disabled:opacity-30" data-testid="upload-close" aria-label="Zapri"><X size={20} /></button>
            </div>
            <input ref={camRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" data-testid="camera-input" onChange={(e) => add(e.target.files, e.target.dataset.retake === "1")} />
            <input ref={galRef} type="file" accept={ACCEPT} multiple className="hidden" data-testid="gallery-input" onChange={(e) => add(e.target.files)} />

            <div className="px-6">
              {step === "choose" && (
                <div className="space-y-4 py-4" data-testid="upload-step-choose">
                  <h2 className="font-display text-3xl">Dodaj fotografije in videe</h2>
                  <button onClick={() => pick(camRef, true)} className="w-full flex items-center gap-4 p-5 rounded-2xl bg-[#231F20] text-white text-left active:scale-[0.99] transition-transform" data-testid="guest-camera-upload-button">
                    <span className="w-14 h-14 rounded-full bg-white/10 grid place-items-center shrink-0"><Camera size={26} /></span>
                    <span><span className="block text-lg font-medium">Fotografiraj ali posnemi video</span><span className="text-sm text-white/60">Odpre se kamera telefona</span></span>
                  </button>
                  <button onClick={() => pick(galRef, false)} className="w-full flex items-center gap-4 p-5 rounded-2xl bg-[#FFFDF9] border border-[#E8DFC5] text-left active:scale-[0.99] transition-transform" data-testid="guest-gallery-upload-button">
                    <span className="w-14 h-14 rounded-full bg-[#F3ECE1] grid place-items-center shrink-0 text-[#B39264]"><Images size={26} /></span>
                    <span><span className="block text-lg font-medium">Izberi iz galerije</span><span className="text-sm text-[#6C6560]">Ena ali več fotografij in videov</span></span>
                  </button>
                  <p className="text-xs text-[#968E87] leading-relaxed">Če se kamera ne odpre neposredno, bo telefon ponudil izbiro med kamero in galerijo. Podprto: JPG, PNG, WEBP, HEIC, MP4, MOV — v originalni kakovosti.</p>
                </div>
              )}

              {step === "review" && (
                <div className="space-y-5 py-2" data-testid="upload-step-review">
                  <h2 className="font-display text-3xl">{files.length === 1 ? "Izbrana datoteka" : `Izbranih ${files.length} datotek`}</h2>
                  <div className="grid grid-cols-3 gap-2">
                    {files.map((f) => (
                      <div key={f.id} className="relative aspect-square rounded-xl overflow-hidden bg-[#F3ECE1]" data-testid="upload-preview-item">
                        <Preview f={f} />
                        <button onClick={() => remove(f.id)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/55 text-white grid place-items-center" data-testid="upload-preview-remove" aria-label="Odstrani"><X size={14} /></button>
                      </div>
                    ))}
                    <button onClick={() => pick(galRef, false)} className="aspect-square rounded-xl border border-dashed border-[#C5A880] grid place-items-center text-[#B39264]" data-testid="upload-add-more"><Plus /></button>
                  </div>
                  {fromCamera.current && (
                    <button onClick={() => pick(camRef, true, true)} className="btn-line w-full py-3 flex items-center justify-center gap-2 text-sm" data-testid="upload-retake"><RotateCcw size={16} /> Ponovno fotografiraj / posnemi</button>
                  )}
                  <div className="space-y-2">
                    {editName ? (
                      <>
                        <label htmlFor="guest-name" className="text-sm font-medium">Ime in priimek</label>
                        <input id="guest-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="npr. Janez Novak" maxLength={80} autoComplete="name" className="w-full h-14 px-5 rounded-2xl border border-[#DFD5C5] bg-white text-lg focus:outline-none focus:ring-2 focus:ring-[#C5A880]" data-testid="guest-name-input" />
                      </>
                    ) : (
                      <p className="text-sm text-[#6C6560]" data-testid="guest-name-display">Nalagaš kot <span className="text-[#231F20] font-medium">{name}</span> · <button onClick={() => setEditName(true)} className="underline underline-offset-2 text-[#B39264]" data-testid="guest-name-change">Spremeni ime</button></p>
                    )}
                  </div>
                  <p className="text-xs text-[#6C6560] bg-[#F3ECE1] rounded-xl p-3 leading-relaxed" data-testid="upload-privacy-notice">
                    Naložene fotografije in videi bodo vidni vsem, ki imajo povezavo ali QR kodo te poroke. Tvoje ime bo prikazano ob datotekah. <Link to="/pravno/zasebnost" target="_blank" className="underline">Zasebnost</Link>
                  </p>
                  <button onClick={start} disabled={name.trim().length < 2} className="btn-dark w-full h-14 text-lg disabled:opacity-40" data-testid="upload-submit-button">Naloži spomine</button>
                </div>
              )}

              {step === "uploading" && (
                <div className="space-y-5 py-2" data-testid="upload-step-progress">
                  <h2 className="font-display text-3xl" data-testid="guest-upload-progress">
                    {running ? `Nalaganje ${current} od ${files.length} ${allPhotos ? "fotografij" : "datotek"} …` : `${failed.length} ${failed.length === 1 ? "datoteka ni bila naložena" : "datotek ni bilo naloženih"}`}
                  </h2>
                  <div className="h-2 rounded-full bg-[#EADBCE] overflow-hidden"><div className="h-full bg-[#C5A880] transition-[width] duration-300" style={{ width: `${overall * 100}%` }} /></div>
                  <ul className="space-y-2 max-h-[40vh] overflow-y-auto">
                    {files.map((f) => (
                      <li key={f.id} className="flex items-center gap-3 text-sm" data-testid="upload-progress-item">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#F3ECE1] shrink-0"><Preview f={f} /></div>
                        <div className="flex-1 min-w-0">
                          <p className="truncate">{f.file.name}</p>
                          {f.error && <p className={`text-xs ${f.status === "error" ? "text-red-700" : "text-[#968E87]"}`}>{f.error}</p>}
                        </div>
                        {f.status === "done" && <Check size={18} className="text-[#7C9A6B]" />}
                        {f.status === "uploading" && <span className="text-xs text-[#6C6560] flex items-center gap-1"><Loader2 size={14} className="animate-spin" />{Math.round(f.progress * 100)}%</span>}
                        {f.status === "error" && <AlertCircle size={18} className="text-red-700" />}
                      </li>
                    ))}
                  </ul>
                  {!running && failed.length > 0 && (
                    <div className="space-y-2">
                      <button onClick={start} className="btn-dark w-full h-14" data-testid="upload-retry-button">Poskusi znova</button>
                      <p className="text-xs text-center text-[#968E87]">Izbrane datoteke ostanejo shranjene, dokler je ta stran odprta.</p>
                    </div>
                  )}
                  {running && <p className="text-xs text-center text-[#968E87]">Prosimo, ne zapiraj te strani, dokler se nalaganje ne zaključi.</p>}
                </div>
              )}

              {step === "done" && (
                <div className="text-center space-y-6 py-8" data-testid="upload-step-done">
                  <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-20 h-20 mx-auto rounded-full bg-[#F3ECE1] grid place-items-center"><Heart size={36} className="fill-[#C99A8E] text-[#C99A8E]" /></motion.div>
                  <h2 className="font-display text-3xl leading-tight" data-testid="upload-thank-you">{wedding.thank_you_message || "Hvala, ker ustvarjaš spomine z nama!"}</h2>
                  <p className="text-sm text-[#6C6560]">{doneCount === 1 ? "1 spomin je dodan" : `${doneCount} spominov je dodanih`} v skupno galerijo.</p>
                  <div className="grid gap-3">
                    <button onClick={() => { reset(); onShowGallery(); }} className="btn-dark h-14" data-testid="upload-done-view-gallery">Poglej galerijo</button>
                    <button onClick={reset} className="btn-line h-14" data-testid="upload-done-add-more">Dodaj še fotografije</button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
